import { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crown, MapPin, Truck, Bluetooth, Package, Eye, EyeSlash, Pulse, X, Lightning, Path, MapTrifold, CheckCircle, Flag, BatteryLow, Sparkle, Rewind, Play, SkipForward, Warning, Target, Lock } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PORTUGAL_CENTER = [39.6, -8.0];
const PORTUGAL_ZOOM = 7;

const REGIONS = [
  { id: "all", label: "Portugal", center: [39.6, -8.0], zoom: 7 },
  { id: "norte", label: "Norte", center: [41.4, -8.3], zoom: 9 },
  { id: "centro", label: "Centro", center: [40.0, -8.4], zoom: 9 },
  { id: "lisboa", label: "Lisboa", center: [38.74, -9.13], zoom: 11 },
  { id: "alentejo", label: "Alentejo", center: [38.5, -7.9], zoom: 9 },
  { id: "algarve", label: "Algarve", center: [37.1, -8.0], zoom: 10 },
];

const STATUS_COLORS = {
  in_transit: "#10b981",
  awaiting_pickup: "#f59e0b",
  at_delivery_point: "#3b82f6",
  delivered: "#6b7280",
  available: "#14b8a6",
};

const EVENT_CONFIG = {
  delivered:        { icon: CheckCircle, color: "#10b981", label: "ENTREGUE" },
  halfway:          { icon: Flag,        color: "#3b82f6", label: "A MEIO" },
  vehicle_arrival:  { icon: Truck,       color: "#f59e0b", label: "CHEGADA" },
  low_battery:      { icon: BatteryLow,  color: "#dc2626", label: "BATERIA" },
  pickup:           { icon: Package,     color: "#22d3ee", label: "RECOLHA" },
  system_online:    { icon: Sparkle,     color: "#a78bfa", label: "SISTEMA" },
};

function isValidLatLng(lat, lng) {
  return typeof lat === "number" && typeof lng === "number" &&
    !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
}

// Smooth animation: interpolate between server snapshots client-side
function useSmoothPositions(items, key) {
  const [smooth, setSmooth] = useState({});
  const targetsRef = useRef({});
  const previousRef = useRef({});

  useEffect(() => {
    const targets = { ...targetsRef.current };
    const prev = { ...previousRef.current };
    items.forEach((it) => {
      const id = it[key];
      if (!isValidLatLng(it.current_lat, it.current_lng)) return;
      if (targets[id]) prev[id] = targets[id];
      else if (!prev[id]) prev[id] = { lat: it.current_lat, lng: it.current_lng, ts: Date.now() };
      targets[id] = { lat: it.current_lat, lng: it.current_lng, ts: Date.now() };
    });
    const validIds = new Set(items.map(it => it[key]));
    Object.keys(targets).forEach(id => { if (!validIds.has(id)) { delete targets[id]; delete prev[id]; } });
    targetsRef.current = targets;
    previousRef.current = prev;
  }, [items, key]);

  useEffect(() => {
    const SERVER_INTERVAL = 2000;
    const FPS = 30;
    const interval = setInterval(() => {
      const now = Date.now();
      const next = {};
      Object.keys(targetsRef.current).forEach((id) => {
        const target = targetsRef.current[id];
        const prev = previousRef.current[id] || target;
        if (!target || !isValidLatLng(target.lat, target.lng) || !isValidLatLng(prev.lat, prev.lng)) return;
        const t = Math.min(1, Math.max(0, (now - prev.ts) / SERVER_INTERVAL));
        const lat = prev.lat + (target.lat - prev.lat) * t;
        const lng = prev.lng + (target.lng - prev.lng) * t;
        if (isValidLatLng(lat, lng)) next[id] = { lat, lng };
      });
      setSmooth(next);
    }, 1000 / FPS);
    return () => clearInterval(interval);
  }, []);

  return smooth;
}

function buildVehicleIcon(vehicleType, status, plate) {
  const color = status === "active" ? "#10b981" : status === "en_route" ? "#f59e0b" : "#6b7280";
  const emoji = { van: "🚐", bus: "🚌", motorcycle: "🛵", bicycle: "🚲", train: "🚆" }[vehicleType] || "🚐";
  const html = `
    <div class="op-vehicle-marker" style="--c:${color}">
      <div class="op-vehicle-pulse"></div>
      <div class="op-vehicle-icon">${emoji}</div>
      <div class="op-vehicle-label">${plate}</div>
    </div>`;
  return L.divIcon({ html, className: "op-vehicle-divicon", iconSize: [44, 44], iconAnchor: [22, 22] });
}

function buildShipmentIcon(status, code) {
  const color = STATUS_COLORS[status] || "#3b82f6";
  const html = `
    <div class="op-bag-marker" style="--c:${color}">
      <div class="op-bag-trail"></div>
      <div class="op-bag-core">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M6 2v2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2H6zm0 2h12v2H6V4zM4 6h16v14H4V6z"/></svg>
      </div>
      <div class="op-bag-label">${code}</div>
    </div>`;
  return L.divIcon({ html, className: "op-bag-divicon", iconSize: [40, 40], iconAnchor: [20, 20] });
}

function buildPointIcon(activityScore) {
  const intensity = Math.min(1, activityScore / 15);
  const html = `
    <div class="op-point-marker" style="--intensity:${intensity}">
      <div class="op-point-ring"></div>
      <div class="op-point-dot"></div>
    </div>`;
  return L.divIcon({ html, className: "op-point-divicon", iconSize: [16, 16], iconAnchor: [8, 8] });
}

// Sparkline (last N values, normalized 0..1)
function Sparkline({ values, color = "#10b981", width = 200, height = 40 }) {
  if (!values || values.length < 2) {
    return <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", opacity: 0.5 }}>aguardando dados...</div>;
  }
  const min = 0, max = 1;
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / (max - min)) * height).toFixed(1)}`).join(" ");
  const last = values[values.length - 1];
  const lastX = (values.length - 1) * step;
  const lastY = height - ((last - min) / (max - min)) * height;
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <defs>
        <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts} ${width},${height}`} fill="url(#sparkfill)" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
    </svg>
  );
}

// Plays a subtle blip when delivery happens
function playDeliveryBlip() {
  try {
    // eslint-disable-next-line no-undef
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.45);
    setTimeout(() => { try { ctx.close(); } catch (e) {} }, 600);
  } catch (e) { /* noop */ }
}

function FlyToHandler({ flyTo }) {
  const map = useMap();
  useEffect(() => {
    if (flyTo && isValidLatLng(flyTo.lat, flyTo.lng)) map.flyTo([flyTo.lat, flyTo.lng], flyTo.zoom || 11, { duration: 1.5 });
  }, [flyTo, map]);
  return null;
}

// Animated trail polyline with progressive opacity
function TrailPolyline({ positions, color }) {
  if (!positions || positions.length < 2) return null;
  // Render a few stacked polylines with decreasing opacity for a "fading tail" effect
  const segments = 4;
  const lines = [];
  for (let i = 0; i < segments; i++) {
    const start = Math.max(0, Math.floor(positions.length * (i / segments)));
    const end = positions.length;
    const opacity = 0.15 + (i / segments) * 0.6;
    const weight = 1.5 + (i / segments) * 2.5;
    lines.push({ start, end, opacity, weight });
  }
  return (
    <>
      {lines.map((l, i) => (
        <Polyline
          key={i}
          positions={positions.slice(l.start, l.end)}
          pathOptions={{ color, weight: l.weight, opacity: l.opacity, lineCap: "round", lineJoin: "round" }}
        />
      ))}
    </>
  );
}

// Floating event toast
function EventToast({ event, onDone }) {
  const cfg = EVENT_CONFIG[event.type] || { icon: Sparkle, color: "#9ca3af", label: event.type.toUpperCase() };
  const Icon = cfg.icon;
  useEffect(() => {
    const t = setTimeout(onDone, 7000);
    return () => clearTimeout(t);
  }, [onDone]);

  let main, sub;
  if (event.type === "delivered") {
    main = `${event.bag_code || event.tracking_code} entregue`;
    sub = event.location || "";
  } else if (event.type === "halfway") {
    main = `${event.bag_code} a meio caminho`;
    sub = `${event.origin} → ${event.destination}`;
  } else if (event.type === "vehicle_arrival") {
    main = `${event.plate} chegou`;
    sub = event.location;
  } else if (event.type === "low_battery") {
    main = `${event.bag_code} bateria baixa`;
    sub = `${event.battery}% restante`;
  } else if (event.type === "pickup") {
    main = `${event.bag_code} recolhida`;
    sub = event.location;
  } else if (event.type === "system_online") {
    main = "Centro de operações online";
    sub = "A monitorizar 100% da rede em Portugal";
  } else {
    main = event.bag_code || event.plate || event.type;
    sub = "";
  }

  return (
    <div className="op-event-toast" style={{ "--c": cfg.color }}>
      <div className="op-event-icon"><Icon size={16} weight="fill" /></div>
      <div className="op-event-body">
        <div className="op-event-label">{cfg.label}</div>
        <div className="op-event-main">{main}</div>
        {sub && <div className="op-event-sub">{sub}</div>}
      </div>
    </div>
  );
}

export default function OperationsCenter({ onClose, userRole }) {
  const [data, setData] = useState({
    points: [], beacons: [], shipments: [], vehicles: [],
    trails: { shipments: {}, vehicles: {} }, events: [],
    tick: 0, timestamp: null
  });
  const [layers, setLayers] = useState({ shipments: true, vehicles: true, points: true, beacons: false, routes: true, trails: true, tamper: false });
  const [tamperPoints, setTamperPoints] = useState([]);
  const [selected, setSelected] = useState(null);
  const [flyTo, setFlyTo] = useState(null);
  const [activeRegion, setActiveRegion] = useState("all");
  const [wsStatus, setWsStatus] = useState("connecting");
  const [activeEvents, setActiveEvents] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [progressHistory, setProgressHistory] = useState({});
  // Replay state
  const [snapshotBuffer, setSnapshotBuffer] = useState([]); // array of {tick, shipments, vehicles, trails}
  const [replayMode, setReplayMode] = useState(false);
  const [replayIdx, setReplayIdx] = useState(0);
  const [replayPlaying, setReplayPlaying] = useState(false);
  // Super admin command state
  const [delayInfo, setDelayInfo] = useState(null);
  const [commandBusy, setCommandBusy] = useState(false);
  const seenEventIds = useRef(new Set());
  const wsRef = useRef(null);

  const isSuperAdmin = userRole === "super_admin";
  const token = localStorage.getItem("blumove_token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Initial HTTP fetch
  useEffect(() => {
    axios.get(`${API}/admin/live-map`, { headers })
      .then(res => {
        setData(res.data);
        // Mark initial events as seen so they don't all popup
        (res.data.events || []).forEach(e => seenEventIds.current.add(e.id));
      })
      .catch(e => console.error("live-map fetch failed", e));
  }, [headers]);

  // Tamper heatmap polling: every 30s when tamper layer is active
  useEffect(() => {
    if (!layers.tamper) return;
    let alive = true;
    const fetchTamper = async () => {
      try {
        const r = await axios.get(`${API}/beacons/tamper-heatmap?hours=24`);
        if (alive) setTamperPoints(r.data.points || []);
      } catch (e) { /* ignore */ }
    };
    fetchTamper();
    const id = setInterval(fetchTamper, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [layers.tamper]);

  // WebSocket for live updates
  useEffect(() => {
    const wsUrl = (BACKEND_URL || window.location.origin).replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/live-map`);
    wsRef.current = ws;
    ws.onopen = () => setWsStatus("live");
    ws.onclose = () => setWsStatus("disconnected");
    ws.onerror = () => setWsStatus("error");
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "live_snapshot") {
          // Skip live updates while replaying
          if (!replayMode) {
            setData(prev => ({
              ...prev,
              shipments: msg.shipments,
              vehicles: msg.vehicles,
              trails: msg.trails || prev.trails,
              events: msg.events || prev.events,
              tick: msg.tick,
              timestamp: msg.timestamp
            }));
          }

          // Record progress history (for sparklines) and ring-buffer
          setProgressHistory(prev => {
            const next = { ...prev };
            (msg.shipments || []).forEach(s => {
              const arr = next[s.tracking_code] || [];
              const last = arr[arr.length - 1];
              if (last !== s.progress) arr.push(typeof s.progress === "number" ? s.progress : 0);
              next[s.tracking_code] = arr.slice(-30);
            });
            return next;
          });

          setSnapshotBuffer(prev => {
            const buf = [...prev, { tick: msg.tick, timestamp: msg.timestamp,
              shipments: msg.shipments, vehicles: msg.vehicles, trails: msg.trails || {} }];
            return buf.slice(-60); // keep last 60 ticks (~2 min @2s tick)
          });

          // Pop new events as toasts + delivery blip
          (msg.events || []).forEach(e => {
            if (!seenEventIds.current.has(e.id)) {
              seenEventIds.current.add(e.id);
              setActiveEvents(prev => [e, ...prev].slice(0, 5));
              if (e.type === "delivered" && soundOn) playDeliveryBlip();
            }
          });
        }
      } catch (e) { /* noop */ }
    };
    return () => { try { ws.close(); } catch (e) {} };
  }, [replayMode, soundOn]);

  // Replay-aware view: override data when replaying
  const viewData = replayMode && snapshotBuffer.length > 0
    ? { ...data, ...snapshotBuffer[Math.min(replayIdx, snapshotBuffer.length - 1)] }
    : data;

  // Auto-play replay
  useEffect(() => {
    if (!replayMode || !replayPlaying) return;
    const id = setInterval(() => {
      setReplayIdx(i => {
        if (i >= snapshotBuffer.length - 1) { setReplayPlaying(false); return i; }
        return i + 1;
      });
    }, 600);
    return () => clearInterval(id);
  }, [replayMode, replayPlaying, snapshotBuffer.length]);

  const smoothShipments = useSmoothPositions(viewData.shipments, "tracking_code");
  const smoothVehicles = useSmoothPositions(viewData.vehicles, "plate");

  const inTransitCount = viewData.shipments.filter(s => s.status === "in_transit").length;
  const activeVehicles = viewData.vehicles.filter(v => v.status === "active" || v.status === "en_route").length;
  const deliveredCount = viewData.events.filter(e => e.type === "delivered").length;

  // Heuristic: shipment is at risk if progress > 0.85 and still in_transit OR slow speed
  const isAtRisk = (s) => s.status === "in_transit" && (
    (typeof s.progress === "number" && s.progress > 0.92) ||
    (typeof s.speed_kmh === "number" && s.speed_kmh < 12)
  );

  const handleRegionClick = (r) => {
    setActiveRegion(r.id);
    setFlyTo({ lat: r.center[0], lng: r.center[1], zoom: r.zoom });
  };

  // --- Super admin commands ---
  const runForceDeliver = async (trackingCode) => {
    setCommandBusy(true);
    try {
      await axios.post(`${API}/admin/shipments/${trackingCode}/force-deliver`, {}, { headers });
      playDeliveryBlip();
    } catch (e) { console.error(e); }
    finally { setCommandBusy(false); }
  };

  const runReroute = async (trackingCode) => {
    if (!data.points || data.points.length === 0) return;
    // Pick a random other point
    const candidates = data.points.filter(p => p.name !== (selected?.data?.destination?.name));
    if (!candidates.length) return;
    const newPoint = candidates[Math.floor(Math.random() * candidates.length)];
    setCommandBusy(true);
    try {
      await axios.post(`${API}/admin/shipments/${trackingCode}/reroute`, { new_delivery_point_id: newPoint.id, reason: "Otimizacao em tempo real" }, { headers });
    } catch (e) { console.error(e); }
    finally { setCommandBusy(false); }
  };

  const runDelayPrediction = async (trackingCode) => {
    setCommandBusy(true);
    try {
      const res = await axios.get(`${API}/admin/shipments/${trackingCode}/delay-prediction`, { headers });
      setDelayInfo(res.data);
    } catch (e) { console.error(e); }
    finally { setCommandBusy(false); }
  };

  return (
    <div className="op-center-root">
      <MapContainer center={PORTUGAL_CENTER} zoom={PORTUGAL_ZOOM} className="op-leaflet-map" zoomControl={false} attributionControl={false}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          subdomains="abc"
          maxZoom={19}
        />
        <FlyToHandler flyTo={flyTo} />

        {/* Activity halos around collection points (intensity based on load) */}
        {layers.points && data.points.filter(p => isValidLatLng(p.lat, p.lng)).map(p => {
          const activity = (p.active_bags || 0) + (p.pending_pickups || 0) + (p.pending_deliveries || 0);
          const radius = 1500 + activity * 800; // meters
          return (
            <Circle
              key={`halo-${p.id || p.name}`}
              center={[p.lat, p.lng]}
              radius={radius}
              pathOptions={{ color: "#22d3ee", fillColor: "#22d3ee", fillOpacity: 0.04 + Math.min(0.12, activity * 0.012), weight: 0.5, opacity: 0.4 }}
            />
          );
        })}

        {/* Collection points (markers on top of halos) */}
        {layers.points && data.points.filter(p => isValidLatLng(p.lat, p.lng)).map(p => {
          const activity = (p.active_bags || 0) + (p.pending_pickups || 0) + (p.pending_deliveries || 0);
          return (
            <Marker key={p.id || p.name} position={[p.lat, p.lng]} icon={buildPointIcon(activity)}>
              <Popup>
                <div style={{ fontFamily: "Manrope" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{p.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>{p.city} · {p.address}</div>
                  <div style={{ fontSize: "0.7rem", marginTop: 4 }}>
                    {p.active_bags} bolsas · {p.pending_pickups} recolhas · {p.pending_deliveries} entregas
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Beacons */}
        {layers.beacons && data.beacons.filter(b => isValidLatLng(b.lat, b.lng)).map(b => (
          <CircleMarker
            key={b.bag_code}
            center={[b.lat, b.lng]}
            radius={5}
            pathOptions={{ color: b.status === "online" ? "#22d3ee" : "#dc2626", fillColor: b.status === "online" ? "#22d3ee" : "#dc2626", fillOpacity: 0.7, weight: 1 }}
          >
            <Popup><div style={{ fontFamily: "Manrope" }}>
              <strong>{b.bag_code}</strong><br />Beacon · {b.status} · {b.battery_level}%
            </div></Popup>
          </CircleMarker>
        ))}

        {/* Active shipment routes */}
        {layers.routes && viewData.shipments.filter(s => s.status === "in_transit" && s.origin && s.destination &&
            isValidLatLng(s.origin.lat, s.origin.lng) && isValidLatLng(s.destination.lat, s.destination.lng)).map(s => (
          <Polyline
            key={`route-${s.tracking_code}`}
            positions={[[s.origin.lat, s.origin.lng], [s.destination.lat, s.destination.lng]]}
            pathOptions={{ color: "#10b981", weight: 2, opacity: 0.35, dashArray: "6 8", className: "op-route-line" }}
          />
        ))}

        {/* Trails (shipments) */}
        {layers.trails && viewData.shipments.filter(s => s.status === "in_transit").map(s => {
          const trail = viewData.trails?.shipments?.[s.tracking_code];
          if (!trail || trail.length < 2) return null;
          const valid = trail.filter(p => Array.isArray(p) && p.length === 2 && isValidLatLng(p[0], p[1]));
          return <TrailPolyline key={`trail-${s.tracking_code}`} positions={valid} color={STATUS_COLORS.in_transit} />;
        })}

        {/* Trails (vehicles) */}
        {layers.trails && viewData.vehicles.map(v => {
          const trail = viewData.trails?.vehicles?.[v.plate];
          if (!trail || trail.length < 2) return null;
          const valid = trail.filter(p => Array.isArray(p) && p.length === 2 && isValidLatLng(p[0], p[1]));
          return <TrailPolyline key={`vtrail-${v.plate}`} positions={valid} color="#f59e0b" />;
        })}

        {/* Shipments */}
        {layers.shipments && viewData.shipments.map(s => {
          const pos = smoothShipments[s.tracking_code];
          if (!pos || !isValidLatLng(pos.lat, pos.lng)) return null;
          return (
            <Marker
              key={`ship-${s.tracking_code}`}
              position={[pos.lat, pos.lng]}
              icon={buildShipmentIcon(s.status, s.bag_code)}
              eventHandlers={{ click: () => setSelected({ kind: "shipment", data: s }) }}
            />
          );
        })}

        {/* Vehicles */}
        {layers.vehicles && viewData.vehicles.map(v => {
          const pos = smoothVehicles[v.plate];
          if (!pos || !isValidLatLng(pos.lat, pos.lng)) return null;
          return (
            <Marker
              key={`veh-${v.plate}`}
              position={[pos.lat, pos.lng]}
              icon={buildVehicleIcon(v.vehicle_type, v.status, v.plate)}
              eventHandlers={{ click: () => setSelected({ kind: "vehicle", data: v }) }}
            />
          );
        })}

        {/* Tamper heatmap layer (last 24h) */}
        {layers.tamper && tamperPoints.filter(p => isValidLatLng(p.lat, p.lng)).map((p, idx) => {
          // Outer halo for visibility
          const halo = 1500 + Math.min(3000, (p.intensity || 1) * 500);
          return (
            <Circle
              key={`tamper-${idx}`}
              center={[p.lat, p.lng]}
              radius={halo}
              pathOptions={{ color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.12, weight: 0.5, opacity: 0.5 }}
            />
          );
        })}
        {layers.tamper && tamperPoints.filter(p => isValidLatLng(p.lat, p.lng)).map((p, idx) => (
          <CircleMarker
            key={`tamper-pt-${idx}`}
            center={[p.lat, p.lng]}
            radius={4 + Math.min(8, (p.intensity || 1) * 2)}
            pathOptions={{ color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.85, weight: 1 }}
          >
            <Popup>
              <div style={{ fontFamily: "Manrope" }}>
                <strong style={{ color: "#dc2626" }}>{p.bag_code}</strong><br />
                Tentativa{p.intensity ? ` · ${p.intensity}g` : ""}<br />
                <span style={{ fontSize: "0.7rem", color: "#6b7280" }}>{new Date(p.created_at).toLocaleString("pt-PT")}</span>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>

      {/* Top HUD */}
      <div className="op-hud-top">
        <div className="op-hud-brand">
          <Crown size={18} weight="fill" />
          <div>
            <div style={{ fontWeight: 800, letterSpacing: "0.04em", fontSize: "0.78rem" }}>BLUMOVE OPS</div>
            <div style={{ fontSize: "0.65rem", opacity: 0.6, letterSpacing: "0.08em" }}>CENTRO DE OPERAÇÕES · PORTUGAL</div>
          </div>
        </div>
        <div className="op-hud-status">
          <span className={`op-status-dot ${wsStatus}`}></span>
          <span style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {wsStatus === "live" ? "Live · tick " + data.tick : wsStatus}
          </span>
        </div>
        <button className="op-close-btn" onClick={onClose} aria-label="Fechar">
          <X size={18} weight="bold" />
        </button>
      </div>

      {/* Region quick-zoom bar (bottom center) */}
      <div className="op-region-bar">
        {REGIONS.map(r => (
          <button
            key={r.id}
            className={`op-region-btn ${activeRegion === r.id ? "active" : ""}`}
            onClick={() => handleRegionClick(r)}
            data-testid={`region-${r.id}`}
          >
            <MapTrifold size={11} weight={activeRegion === r.id ? "fill" : "regular"} />
            {r.label}
          </button>
        ))}
      </div>

      {/* Live event toasts (right-center, slide-in) */}
      <div className="op-event-feed">
        {activeEvents.map(e => (
          <EventToast
            key={e.id}
            event={e}
            onDone={() => setActiveEvents(prev => prev.filter(x => x.id !== e.id))}
          />
        ))}
      </div>

      {/* Left HUD - KPIs + Layers */}
      <div className="op-hud-left">
        <div className="op-kpi-card">
          <div className="op-kpi-value">{inTransitCount}</div>
          <div className="op-kpi-label"><Pulse size={12} weight="bold" /> EM TRÂNSITO</div>
        </div>
        <div className="op-kpi-card">
          <div className="op-kpi-value">{activeVehicles}</div>
          <div className="op-kpi-label"><Truck size={12} weight="bold" /> VEÍCULOS ATIVOS</div>
        </div>
        <div className="op-kpi-card">
          <div className="op-kpi-value">{data.points.length}</div>
          <div className="op-kpi-label"><MapPin size={12} weight="bold" /> PONTOS DE REDE</div>
        </div>
        <div className="op-kpi-card op-kpi-success">
          <div className="op-kpi-value">{deliveredCount}</div>
          <div className="op-kpi-label"><CheckCircle size={12} weight="bold" /> ENTREGAS RECENTES</div>
        </div>

        <div className="op-layer-section">
          <div className="op-layer-title">CAMADAS</div>
          {[
            { key: "shipments", label: "Bolsas", icon: Package },
            { key: "vehicles", label: "Veículos", icon: Truck },
            { key: "trails", label: "Rastos", icon: Sparkle },
            { key: "routes", label: "Rotas planeadas", icon: Path },
            { key: "points", label: "Pontos + halos", icon: MapPin },
            { key: "beacons", label: "Beacons (BLE)", icon: Bluetooth },
            { key: "tamper", label: "Heatmap tamper (24h)", icon: Lock },
          ].map(l => (
            <button
              key={l.key}
              data-testid={`op-layer-toggle-${l.key}`}
              className={`op-layer-btn ${layers[l.key] ? "active" : ""}`}
              onClick={() => setLayers(s => ({ ...s, [l.key]: !s[l.key] }))}
            >
              <l.icon size={14} weight={layers[l.key] ? "fill" : "regular"} />
              <span>{l.label}</span>
              {layers[l.key] ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} weight="bold" />}
            </button>
          ))}
        </div>
      </div>

      {/* Right HUD */}
      <div className="op-hud-right">
        <div className="op-list-header">
          <Pulse size={14} weight="fill" /> BOLSAS EM TRÂNSITO ({inTransitCount})
        </div>
        <div className="op-list-body">
          {viewData.shipments.filter(s => s.status === "in_transit").map(s => {
            const atRisk = isAtRisk(s);
            return (
              <button
                key={s.tracking_code}
                className={`op-ship-item ${atRisk ? "at-risk" : ""}`}
                onClick={() => setFlyTo({ lat: s.current_lat, lng: s.current_lng, zoom: 11 })}
                data-testid={`ship-item-${s.tracking_code}`}
              >
                <div className="op-ship-row">
                  <span className="op-ship-code">{s.bag_code}</span>
                  {atRisk && <span className="op-risk-pill"><Warning size={9} weight="fill" /> RISCO</span>}
                  <span className="op-ship-speed"><Lightning size={10} weight="fill" /> {s.speed_kmh} km/h</span>
                </div>
                <div className="op-ship-route">{s.origin.city} → {s.destination.city}</div>
                <div className="op-ship-progress">
                  <div className="op-progress-bar"><div className="op-progress-fill" style={{ width: `${(s.progress * 100).toFixed(1)}%` }} /></div>
                  <span style={{ fontSize: "0.62rem", opacity: 0.7 }}>{(s.progress * 100).toFixed(0)}% · {s.distance_km}km</span>
                </div>
              </button>
            );
          })}
          {inTransitCount === 0 && <div style={{ padding: 16, textAlign: "center", fontSize: "0.75rem", opacity: 0.6 }}>Sem bolsas em trânsito.</div>}
        </div>

        <div className="op-list-header" style={{ marginTop: 16 }}>
          <Truck size={14} weight="fill" /> FROTA ({viewData.vehicles.length})
        </div>
        <div className="op-list-body">
          {viewData.vehicles.map(v => (
            <button
              key={v.plate}
              className="op-ship-item"
              onClick={() => setFlyTo({ lat: v.current_lat, lng: v.current_lng, zoom: 11 })}
            >
              <div className="op-ship-row">
                <span className="op-ship-code">{v.plate}</span>
                <span className="op-ship-speed" style={{ color: v.status === "active" ? "#10b981" : "#f59e0b" }}>{v.status}</span>
              </div>
              <div className="op-ship-route">{v.vehicle_type} → {v.destination?.name}</div>
              <div className="op-ship-progress">
                <div className="op-progress-bar"><div className="op-progress-fill" style={{ width: `${(v.progress * 100).toFixed(1)}%`, background: "#f59e0b" }} /></div>
                <span style={{ fontSize: "0.62rem", opacity: 0.7 }}>{(v.progress * 100).toFixed(0)}% · {v.bags_on_board?.length || 0} bolsa(s)</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected detail card */}
      {selected && (
        <div className={`op-detail-card ${selected.kind === "shipment" && isAtRisk(selected.data) ? "at-risk" : ""}`}>
          <button className="op-detail-close" onClick={() => { setSelected(null); setDelayInfo(null); }}><X size={14} weight="bold" /></button>
          {selected.kind === "shipment" ? (
            <>
              <div className="op-detail-header"><Package size={14} weight="fill" /> ENVIO</div>
              <div className="op-detail-title">{selected.data.tracking_code}</div>
              <div className="op-detail-row"><span>Bolsa</span><strong>{selected.data.bag_code}</strong></div>
              <div className="op-detail-row"><span>Estado</span><strong style={{ color: STATUS_COLORS[selected.data.status] }}>{selected.data.status}</strong></div>
              <div className="op-detail-row"><span>Origem</span><strong>{selected.data.origin?.name}</strong></div>
              <div className="op-detail-row"><span>Destino</span><strong>{selected.data.destination?.name}</strong></div>
              <div className="op-detail-row"><span>Distância</span><strong>{selected.data.distance_km} km</strong></div>
              <div className="op-detail-row"><span>Velocidade</span><strong>{selected.data.speed_kmh} km/h</strong></div>
              <div className="op-detail-row"><span>Progresso</span><strong>{(selected.data.progress * 100).toFixed(1)}%</strong></div>
              {selected.data.distance_km && selected.data.speed_kmh && (
                <div className="op-detail-row"><span>ETA</span><strong>~{Math.round((selected.data.distance_km * (1 - selected.data.progress)) / selected.data.speed_kmh * 60)} min</strong></div>
              )}

              {/* Sparkline of progress history */}
              <div className="op-spark-wrap">
                <div className="op-spark-label"><Pulse size={10} weight="fill" /> PROGRESSO (últimos {Math.min(30, (progressHistory[selected.data.tracking_code] || []).length)} ticks)</div>
                <Sparkline values={progressHistory[selected.data.tracking_code] || []} color={STATUS_COLORS[selected.data.status] || "#10b981"} width={240} height={42} />
              </div>

              {/* Super Admin commands */}
              {isSuperAdmin && (
                <div className="op-cmd-panel">
                  <div className="op-cmd-title"><Crown size={11} weight="fill" /> COMANDOS SUPER ADMIN</div>
                  <button className="op-cmd-btn" disabled={commandBusy} onClick={() => runDelayPrediction(selected.data.tracking_code)} data-testid="cmd-delay-prediction">
                    <Target size={12} weight="bold" /> Prever atraso
                  </button>
                  <button className="op-cmd-btn warn" disabled={commandBusy} onClick={() => runReroute(selected.data.tracking_code)} data-testid="cmd-reroute">
                    <Path size={12} weight="bold" /> Reencaminhar
                  </button>
                  <button className="op-cmd-btn danger" disabled={commandBusy} onClick={() => runForceDeliver(selected.data.tracking_code)} data-testid="cmd-force-deliver">
                    <CheckCircle size={12} weight="bold" /> Forcar entrega
                  </button>

                  {delayInfo && (
                    <div className={`op-delay-result level-${delayInfo.risk_level}`}>
                      <div className="op-delay-row"><span>Risco</span><strong>{delayInfo.risk_score}/100 · {delayInfo.risk_level.toUpperCase()}</strong></div>
                      {delayInfo.predicted_minutes_late > 0 && (
                        <div className="op-delay-row"><span>Atraso estimado</span><strong>+{delayInfo.predicted_minutes_late} min</strong></div>
                      )}
                      <div className="op-delay-reasons">
                        {delayInfo.reasons.map((r, i) => <div key={i}>· {r}</div>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="op-detail-header"><Truck size={14} weight="fill" /> VEÍCULO</div>
              <div className="op-detail-title">{selected.data.plate}</div>
              <div className="op-detail-row"><span>Tipo</span><strong>{selected.data.vehicle_type}</strong></div>
              <div className="op-detail-row"><span>Estado</span><strong>{selected.data.status}</strong></div>
              <div className="op-detail-row"><span>Velocidade</span><strong>{selected.data.speed_kmh} km/h</strong></div>
              <div className="op-detail-row"><span>Bolsas a bordo</span><strong>{selected.data.bags_on_board?.length || 0}</strong></div>
              <div className="op-detail-row"><span>Destino</span><strong>{selected.data.destination?.name}</strong></div>
            </>
          )}
        </div>
      )}

      {/* Replay scrubber + sound toggle */}
      <div className="op-replay-bar">
        <button
          className={`op-replay-toggle ${replayMode ? "active" : ""}`}
          onClick={() => { setReplayMode(m => !m); setReplayPlaying(false); setReplayIdx(snapshotBuffer.length - 1); }}
          disabled={snapshotBuffer.length < 2}
          data-testid="replay-toggle"
        >
          <Rewind size={12} weight="fill" /> {replayMode ? "REPLAY ON" : "REPLAY"}
        </button>

        {replayMode && (
          <>
            <button className="op-replay-mini" onClick={() => setReplayPlaying(p => !p)} data-testid="replay-play">
              <Play size={12} weight="fill" />
            </button>
            <input
              type="range"
              min={0}
              max={Math.max(0, snapshotBuffer.length - 1)}
              value={replayIdx}
              onChange={(e) => setReplayIdx(parseInt(e.target.value))}
              className="op-replay-scrubber"
              data-testid="replay-scrubber"
            />
            <span className="op-replay-tick">tick {snapshotBuffer[replayIdx]?.tick ?? "-"}</span>
            <button className="op-replay-mini" onClick={() => setReplayIdx(snapshotBuffer.length - 1)}>
              <SkipForward size={12} weight="fill" />
            </button>
          </>
        )}

        <button
          className={`op-sound-toggle ${soundOn ? "on" : "off"}`}
          onClick={() => setSoundOn(s => !s)}
          data-testid="sound-toggle"
          title="Som de entrega"
        >
          {soundOn ? "🔔" : "🔕"}
        </button>
      </div>
    </div>
  );
}
